import { useCallback, useContext } from 'react';
import useGetFieldMetricUsage from './useGetFieldMetricUsage';
import useGetFieldSimpleGridUsage from './useGetFieldSimpleGridUsage';
import useGetFieldGadgetDimensionUsage from './useGetFieldGadgetDimensionUsage';
import useGetFieldReportFilterUsage from './useGetFieldReportFilterUsage';
import useGetFieldDashboardGadgetFilterUsage from './useGetFieldDashboardGadgetFilterUsage';
import useGetFieldMetricListUsage from './useGetFieldMetricListUsage';
import useGetFieldSimpleGridSortUsage from './useGetFieldSimpleGridSortUsage';
import useGetFieldSavedFilterUsage from './useGetFieldSavedFilterUsage';
import useGetFieldDefaultDatasetFilterUsage from './useGetFieldDefaultDatasetFilterUsage';
import { EnrichedFieldUsage, FieldUsageEntities } from '../types';
import useGetFieldReportUsage from './useGetFieldReportUsage';
import useGetFieldDashboardUsage from './useGetFieldDashboardUsage';
import useGetFieldGoalUsage from './useGetFieldGoalUsage';
import useGetFieldScorecardUsage from './useGetFieldScorecardUsage';
import useGetFieldPerformanceBoardUsage from './useGetFieldPerformanceBoardUsage';
import useGetFieldBonusAppUsage from './useGetFieldBonusAppUsage';
import DataDictionaryContext from '../../../../../../contexts/DataDictionaryContext';

const useGetFieldUsage = () => {
  const { selectedDataset } = useContext(DataDictionaryContext);
  const getFieldMetricUsage = useGetFieldMetricUsage();
  const getFieldSimpleGridUsage = useGetFieldSimpleGridUsage();
  const getFieldGadgetDimensionUsage = useGetFieldGadgetDimensionUsage();
  const getFieldReportFilterUsage = useGetFieldReportFilterUsage();
  const getFieldDashboardGadgetFilterUsage =
    useGetFieldDashboardGadgetFilterUsage();
  const getFieldMetricListUsage = useGetFieldMetricListUsage();
  const getFieldSimpleGridSortUsage = useGetFieldSimpleGridSortUsage();
  const getFieldSavedFilterUsage = useGetFieldSavedFilterUsage();
  const getFieldDefaultDatasetFilterUsage =
    useGetFieldDefaultDatasetFilterUsage();
  const getReportUsage = useGetFieldReportUsage();
  const getDashboardUsage = useGetFieldDashboardUsage();
  const getGoalUsage = useGetFieldGoalUsage();
  const getScorecardUsage = useGetFieldScorecardUsage();
  const getPerformanceBoardUsage = useGetFieldPerformanceBoardUsage();
  const getAppUsage = useGetFieldBonusAppUsage();

  const dataset = selectedDataset ? selectedDataset.type : undefined;

  const getFieldUsage = useCallback(
    (field: string): EnrichedFieldUsage => {
      if (!dataset) {
        const e = new Error('dataset not found');
        e.name = 'FieldUsage: Dataset Not Found';
        throw e;
      }

      const metrics = getFieldMetricUsage(field, dataset);
      const simpleGrids = getFieldSimpleGridUsage(field, dataset);
      const gadgetDimensions = getFieldGadgetDimensionUsage(field, dataset);
      const reportFilters = getFieldReportFilterUsage(field, dataset);
      const dashboardGadgetFilters = getFieldDashboardGadgetFilterUsage(
        field,
        dataset,
      );
      const metricLists = getFieldMetricListUsage(field, dataset);
      const simpleGridSorts = getFieldSimpleGridSortUsage(field, dataset);
      const savedFilters = getFieldSavedFilterUsage(field, dataset);
      const defaultDatasetFilters = getFieldDefaultDatasetFilterUsage(
        field,
        dataset,
      );
      const goals = getGoalUsage(field, dataset);
      const scorecards = getScorecardUsage(field, dataset);
      const performanceBoards = getPerformanceBoardUsage(field, dataset);
      const apps = getAppUsage(field, dataset);

      const usage: FieldUsageEntities = {
        metrics,
        metricLists,
        simpleGrids,
        gadgetDimensions,
        reportFilters,
        dashboardGadgetFilters,
        simpleGridSorts,
        savedFilters,
        defaultDatasetFilters,
        goals,
        scorecards,
        performanceBoards,
        ...apps,
      };

      return {
        ...usage,
        reports: getReportUsage(usage),
        dashboards: getDashboardUsage(usage),
      };
    },
    [
      dataset,
      getFieldMetricUsage,
      getFieldSimpleGridUsage,
      getFieldGadgetDimensionUsage,
      getFieldReportFilterUsage,
      getFieldDashboardGadgetFilterUsage,
      getFieldMetricListUsage,
      getFieldSimpleGridSortUsage,
      getFieldSavedFilterUsage,
      getFieldDefaultDatasetFilterUsage,
      getGoalUsage,
      getScorecardUsage,
      getPerformanceBoardUsage,
      getAppUsage,
      getReportUsage,
      getDashboardUsage,
    ],
  );

  return getFieldUsage;
};

export default useGetFieldUsage;
